import { ApolloError } from '@apollo/client';
import { useCallback, useMemo, useState } from 'react';
import { isNullish } from 'remeda';
import { useLandingPageConfig, useSaveLandingPageConfig } from 'src/graphql';
import { type LandingPageFormValues } from 'src/pages/Settings/memberships/components/landing-page-tab/types';
import {
  type ConfigFormSaveRequest,
  type ConfigFormSubmitState,
  type ConfigSurfaceController,
  EMPTY_CONFIG_FORM_SUBMIT_STATE,
} from 'src/pages/Settings/memberships/types';
import { mapLandingPageConfigToFormValues } from 'src/pages/Settings/memberships/utils/map-landing-page-config-to-form-values';
import { parseLandingPageError } from 'src/pages/Settings/memberships/utils/parse-landing-page-error';
import { serializeLandingPageConfig } from 'src/pages/Settings/memberships/utils/serialize-landing-page-config';
import { useConfirmationModal } from 'src/utils/hooks';
import { useNotifications } from 'src/utils/hooks/use-notifications';

const SAVE_FAILED_MESSAGE = 'Failed to save the landing page.';

interface UseLandingPageSurfaceParams {
  partnerId: number | null;
  selectedSegmentId: string | null;
  canManage: boolean;
}

interface LandingPageSurface extends ConfigSurfaceController<LandingPageFormValues> {
  formKey: string;
  data: LandingPageFormValues;
}

export const useLandingPageSurface = ({
  partnerId,
  selectedSegmentId,
  canManage,
}: UseLandingPageSurfaceParams): LandingPageSurface => {
  const { confirm } = useConfirmationModal();
  const { showErrorAlert, showSuccessAlert } = useNotifications();
  const { config, refetch } = useLandingPageConfig(
    { partnerId, brandId: selectedSegmentId },
    { skip: !canManage },
  );
  const { saveLandingPageConfig, loading: saveLoading } = useSaveLandingPageConfig();

  const [requestSaveValues, setRequestSaveValues] =
    useState<ConfigFormSaveRequest<LandingPageFormValues> | null>(null);
  const [submitState, setSubmitState] = useState<ConfigFormSubmitState>(
    EMPTY_CONFIG_FORM_SUBMIT_STATE,
  );
  const [isFormDirty, setIsFormDirty] = useState(false);

  const data = useMemo<LandingPageFormValues>(
    () => mapLandingPageConfigToFormValues(config),
    [config],
  );
  const hasUnsavedChanges = Boolean(requestSaveValues) && isFormDirty;
  const isSaving = submitState.isSubmitting || saveLoading;

  const handleSave = useCallback(async () => {
    if (!canManage || !requestSaveValues) {
      return;
    }

    if (!selectedSegmentId || isNullish(partnerId)) {
      await confirm({ body: 'Select a partner and client segment before saving.' });
      return;
    }

    const savePayload = await requestSaveValues();

    if (!savePayload) {
      await confirm({
        title: 'Fix validation errors',
        body: 'Some fields are invalid. Please fix the errors and try again.',
      });
      return;
    }

    const { values, reset } = savePayload;

    const nextConfig = serializeLandingPageConfig({
      storedConfig: config?.config,
      blocks: values.blocks,
      settings: values.settings,
    });

    try {
      const response = await saveLandingPageConfig({
        variables: {
          input: {
            partnerId,
            brandId: selectedSegmentId,
            name: values.name,
            slug: values.slug,
            config: nextConfig,
          },
        },
      });

      if (!response.data?.saveLandingPageConfig?.success) {
        showErrorAlert(SAVE_FAILED_MESSAGE);
        return;
      }

      reset();
      showSuccessAlert('Landing page saved.');
      await refetch();
    } catch (saveError) {
      showErrorAlert(
        saveError instanceof ApolloError
          ? parseLandingPageError(saveError)
          : SAVE_FAILED_MESSAGE,
      );
    }
  }, [
    canManage,
    confirm,
    config,
    partnerId,
    refetch,
    requestSaveValues,
    saveLandingPageConfig,
    selectedSegmentId,
    showErrorAlert,
    showSuccessAlert,
  ]);

  const saveSlots = useMemo(
    () => ({
      onSaveRequestChange: setRequestSaveValues,
      onDirtyChange: setIsFormDirty,
      onSubmitStateChange: setSubmitState,
    }),
    [],
  );

  return {
    formKey: `${String(partnerId)}:${selectedSegmentId ?? ''}`,
    data,
    saveSlots,
    hasUnsavedChanges,
    isSaving,
    isSaveDisabled: !canManage || !submitState.isValid || isSaving,
    handleSave,
  };
};
